import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

interface WaveformVisualizerProps {
  audioUrl: string;
  width?: number;
  height?: number;
}

const WaveformVisualizer: React.FC<WaveformVisualizerProps> = ({
  audioUrl,
  width = 600,
  height = 200,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const renderer = new THREE.WebGLRenderer();
    renderer.setSize(width, height);
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(
      0,
      width,
      height,
      0,
      -1,
      1
    );
    sceneRef.current = scene;

    const drawWaveform = async () => {
      if (!audioUrl) return;

      try {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        audioContextRef.current = audioContext;

        const response = await fetch(audioUrl);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

        const data = audioBuffer.getChannelData(0);
        const step = Math.ceil(data.length / width);

        const geometry = new THREE.BufferGeometry();
        const vertices: number[] = [];

        for (let i = 0; i < width; i++) {
          const start = i * step;
          const end = (i + 1) * step;
          const segment = data.slice(start, end);
          const min = Math.min(...Array.from(segment));
          const max = Math.max(...Array.from(segment));

          vertices.push(i, (1 + min) * height / 2, 0);
          vertices.push(i, (1 + max) * height / 2, 0);
        }

        geometry.setAttribute(
          'position',
          new THREE.Float32BufferAttribute(vertices, 3)
        );

        const material = new THREE.LineBasicMaterial({ color: 0x3b82f6 });
        const line = new THREE.LineSegments(geometry, material);
        scene.add(line);

        renderer.render(scene, camera);
      } catch (error) {
        console.error('Error drawing waveform:', error);
      }
    };

    drawWaveform();

    return () => {
      if (rendererRef.current) rendererRef.current.dispose();
      if (audioContextRef.current) audioContextRef.current.close();
    };
  }, [audioUrl, width, height]);

  return <div ref={containerRef} />;
};

export default WaveformVisualizer;
